import React, { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import {
  criarComparecimento,
  ComparecimentoCreate,
  Comparecimento,
} from '../../services/api.ts';
import BaseModal from './_BaseModal.tsx';
import {
  getAuth,
  nowDateInputs,
  toISOSimpleString,
  maskCurrencyBRL,
  parseBRLToNumber,
} from './_helpers.ts';

interface AttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number | null | undefined;
  agendamentoId: number | null | undefined;
  contactName: string;
  contactPhone: string;
  clinicId: number;
  onCreated?: (comparecimento: Comparecimento) => void;
}

const AttendanceModal: React.FC<AttendanceModalProps> = ({
  isOpen,
  onClose,
  leadId,
  agendamentoId,
  contactName,
  contactPhone,
  clinicId,
  onCreated,
}) => {
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    date: '',
    time: '',
    tratamento: '',
    orcamento: '',
  });

  useEffect(() => {
    if (isOpen) {
      const { date, time } = nowDateInputs();
      setForm({ date, time, tratamento: '', orcamento: '' });
      setError(null);
    }
  }, [isOpen]);

  const isMissingIds = !leadId || !agendamentoId;
  const canSubmit = !!leadId && !!agendamentoId && !!form.date && !!form.time;

  const handleSubmit = async () => {
    if (!leadId || !agendamentoId || !canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const auth = getAuth(clinicId);
      const payload: ComparecimentoCreate = {
        lead_id: leadId,
        agendamento_id: agendamentoId,
        nome: contactName,
        phone: contactPhone,
        tratamento_orcado: form.tratamento || undefined,
        valor_orcamento: form.orcamento ? parseBRLToNumber(form.orcamento) : undefined,
        compareceu_em: toISOSimpleString(form.date, form.time),
      };
      console.log('[AttendanceModal] Registrando comparecimento', payload);
      const novo = await criarComparecimento(auth.clientId, auth.clinicId, payload);
      console.log('[AttendanceModal] Comparecimento registrado', novo);
      onCreated?.(novo);
      onClose();
    } catch (err: any) {
      console.error('[AttendanceModal] Erro:', err);
      setError(err?.response?.data?.detail || err?.message || 'Erro ao registrar comparecimento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Marcar comparecimento"
      subtitle="Registrar que o lead compareceu à consulta"
      icon={CheckCircle2}
      primaryLabel="Confirmar comparecimento"
      onPrimary={isMissingIds ? undefined : handleSubmit}
      primaryDisabled={!canSubmit}
      saving={saving}
      error={error}
    >
      {isMissingIds ? (
        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Este contato precisa ter um agendamento ativo para registrar comparecimento.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Data do comparecimento
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
            <div className="space-y-2">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Hora
              </label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Tratamento orçado
            </label>
            <input
              type="text"
              value={form.tratamento}
              onChange={(e) => setForm({ ...form, tratamento: e.target.value })}
              placeholder="Ex.: Implante unitário"
              className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>

          <div className="space-y-2">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Valor do orçamento
            </label>
            <input
              type="text"
              value={form.orcamento}
              onChange={(e) => setForm({ ...form, orcamento: maskCurrencyBRL(e.target.value) })}
              placeholder="R$ 0,00"
              className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>
        </>
      )}
    </BaseModal>
  );
};

export default AttendanceModal;
